/**
 * 약관 버전 이력 — 같은 종류의 모든 버전을 최신순으로 보여준다.
 *
 * `endedAt`은 다음 버전의 시행일시이며, 마지막(최신) 버전은 null이다.
 */
import { useTermsVersions } from '@/entities/terms/hooks/useTerms';
import type { TermsType } from '@/entities/terms/api/terms.api';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Badge } from '@/shared/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { termsDetailPath } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { formatDateTime } from '@/shared/lib/dateFormat';
import {
  isCurrentlyEffective,
  isScheduled,
} from '@/pages/terms/termsUtils';
import { handleApiError } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import React, { useEffect } from 'react';

interface TermsVersionHistorySectionProps {
  type: TermsType;
  /** 현재 보고 있는 버전 — 이력에서 강조한다 */
  currentTermsId: number;
  /** 섹션 높이 제약 — 상단 요약 영역에서 카드 높이를 고정할 때 쓴다 */
  className?: string;
}

export const TermsVersionHistorySection: React.FC<
  TermsVersionHistorySectionProps
> = ({ type, currentTermsId, className }) => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useTermsVersions(type);

  useEffect(() => {
    if (isError && error) handleApiError(error);
  }, [isError, error]);

  const versions = data ?? [];

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <h2 className="text-sm font-bold text-foreground">버전 이력</h2>
      <DataCard className="flex-1 overflow-y-auto">
        {isLoading ? (
          <LoadingSpinner />
        ) : versions.length === 0 ? (
          <EmptyState description="등록된 버전이 없습니다." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">버전</TableHead>
                <TableHead>시행일시</TableHead>
                <TableHead>시행 종료일시</TableHead>
                <TableHead>변경 요약</TableHead>
                <TableHead>등록자</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((version) => {
                const isViewing = version.termsId === currentTermsId;
                return (
                  <TableRow
                    key={version.termsId}
                    className="cursor-pointer hover:bg-muted/50"
                    data-state={isViewing ? 'selected' : undefined}
                    onClick={() =>
                      navigate({
                        to: termsDetailPath(version.termsId),
                      } as never)
                    }
                  >
                    <TableCell className="font-medium">
                      <span className="inline-flex flex-wrap items-center gap-1">
                        v{version.version}
                        {isViewing ? <Badge variant="info">보는 중</Badge> : null}
                        {isScheduled(version.effectiveAt) ? (
                          <Badge variant="warning">시행 예정</Badge>
                        ) : isCurrentlyEffective(
                            version.effectiveAt,
                            version.endedAt,
                          ) ? (
                          <Badge variant="success">시행</Badge>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell>{formatDateTime(version.effectiveAt)}</TableCell>
                    <TableCell>{formatDateTime(version.endedAt)}</TableCell>
                    <TableCell>{version.changeSummary || '-'}</TableCell>
                    <TableCell>{version.createdByName || '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DataCard>
    </div>
  );
};
